export type InvestorProfilePayload = Record<string, unknown>;

export type ProfilePayloadInputKind =
  | 'text'
  | 'date'
  | 'month'
  | 'email'
  | 'tel'
  | 'number'
  | 'boolean';

export type ProfilePayloadFormValues = Record<string, string>;

export type ProfilePayloadValueKind = 'scalar' | 'money' | 'percent' | 'name-part' | 'string-list';

export type ProfilePayloadOriginalValue = string | number | boolean | readonly string[] | null;

export type ProfilePayloadInputOption = {
  value: string;
  label: string;
};

export type ProfilePayloadInputField = {
  path: string;
  label: string;
  inputKind: ProfilePayloadInputKind;
  value: string;
  required: boolean;
  originalValue: ProfilePayloadOriginalValue;
  valueKind: ProfilePayloadValueKind;
  currency?: string;
  groupPath?: string;
  options?: ProfilePayloadInputOption[];
};

export type ProfilePayloadPatch = {
  expected_revision: string;
  idempotency_key: string;
  update: Record<string, unknown>;
};

export type ProfilePayloadPatchBuildResult = {
  patch: ProfilePayloadPatch;
  changedPaths: string[];
};

export type ProfilePayloadValidationSeverity = 'error' | 'warning';

export type ProfilePayloadFieldValidationMessage = {
  fieldPath: string;
  severity: ProfilePayloadValidationSeverity;
  message: string;
  relatedFieldPaths?: string[];
};

export type ProfilePayloadValidationResult = {
  messages: ProfilePayloadFieldValidationMessage[];
  messagesByPath: Record<string, ProfilePayloadFieldValidationMessage[]>;
  hasErrors: boolean;
  hasWarnings: boolean;
  firstErrorPath: string | null;
};

const SPECIAL_LABELS: Record<string, string> = {
  'sections.investor.name.given_name': 'First name',
  'sections.investor.name.middle_names': 'Middle names',
  'sections.investor.name.family_name': 'Last name',
  'sections.investor.name.name_suffix': 'Suffix',
  'sections.investor.date_of_birth': 'Date of birth',
  'sections.investor.email': 'Email',
  'sections.investor.phone_number': 'Phone number',
  'sections.investor.primary_residence_state': 'State of domicile',
  'sections.investor.marital_status': 'Marital status',
  'sections.investor.citizenship_status': 'Citizenship status',
  'sections.tax.filing_status': 'Filing status',
  'sections.expenses.total_fixed_monthly': 'Fixed monthly expenses',
  'sections.expenses.total_variable_monthly': 'Variable monthly expenses',
  'sections.expenses.annual_one_time': 'Annual one-time expenses',
  'sections.retirement.target_age': 'Target retirement age',
  'sections.retirement.spouse_target_age': 'Spouse target retirement age',
  'sections.retirement.desired_annual_spending': 'Desired retirement annual spending',
  'sections.income_sources[].custom_source_type': 'Custom source type',
  'sections.income_sources[].amount_tax_basis': 'Tax basis',
  'sections.income_sources[].cola_enabled': 'COLA',
  'sections.income_sources[].cola_percent': 'COLA percent',
  'sections.income_sources[].start_timing': 'Start timing',
  'sections.income_sources[].start_month': 'Start month',
  'sections.income_sources[].claiming_age': 'Claiming age',
  'sections.income_sources[].end_timing': 'End timing',
  'sections.income_sources[].end_month': 'End month',
  'sections.beneficiary_candidates[].source_status': 'Source/status',
  'sections.beneficiary_candidates[].last_verified_date': 'Last verified date',
};

const SPECIAL_INPUT_KINDS: Record<string, ProfilePayloadInputKind> = {
  'sections.investor.date_of_birth': 'date',
  'sections.investor.email': 'email',
  'sections.investor.phone_number': 'tel',
  'sections.retirement.target_age': 'number',
  'sections.retirement.spouse_target_age': 'number',
  'sections.business.owns_business': 'boolean',
};

const SPECIAL_MONEY_FIELD_PATHS = new Set(['sections.retirement.desired_annual_spending']);

const BOOLEAN_FIELD_SUFFIXES = [
  '.joint_with_spouse',
  '.shared_with_spouse',
  '.cola_enabled',
] as const;

const TAX_ANTICIPATED_EVENTS_FIELD_PATH = 'sections.tax.anticipated_events';
const NONE_EXCLUSIVE_STRING_LIST_FIELD_PATHS = new Set([TAX_ANTICIPATED_EVENTS_FIELD_PATH]);

const PROFILE_ENUM_VALUE_FALLBACKS: Record<string, string[]> = {
  'sections.investor.marital_status': [
    'SINGLE',
    'MARRIED',
    'DOMESTIC_PARTNERSHIP',
    'DIVORCED',
    'WIDOWED',
    'SEPARATED',
    'MARITAL_STATUS_UNSPECIFIED',
  ],
  'sections.investor.citizenship': [
    'US_CITIZEN',
    'PERMANENT_RESIDENT',
    'NON_RESIDENT_ALIEN',
    'DUAL_CITIZEN',
  ],
  'sections.investor.citizenship_status': [
    'US_CITIZEN',
    'PERMANENT_RESIDENT',
    'NON_RESIDENT_ALIEN',
    'DUAL_CITIZEN',
  ],
  'sections.investor.residency_status': [
    'US_CITIZEN',
    'PERMANENT_RESIDENT',
    'NON_RESIDENT_ALIEN',
    'DUAL_CITIZEN',
  ],
  'sections.investor.employment.employment_status': [
    'EMPLOYED',
    'SELF_EMPLOYED',
    'BUSINESS_OWNER',
    'UNEMPLOYED',
    'RETIRED',
    'NOT_WORKING',
    'STUDENT',
    'HOMEMAKER',
    'EMPLOYMENT_STATUS_UNSPECIFIED',
  ],
  'sections.spouse.employment.employment_status': [
    'EMPLOYED',
    'SELF_EMPLOYED',
    'BUSINESS_OWNER',
    'UNEMPLOYED',
    'RETIRED',
    'NOT_WORKING',
    'STUDENT',
    'HOMEMAKER',
    'EMPLOYMENT_STATUS_UNSPECIFIED',
  ],
  'sections.dependents[].relationship': [
    'CHILD',
    'STEPCHILD',
    'ADOPTED_CHILD',
    'PARENT',
    'OTHER_DEPENDENT',
  ],
  'sections.income_sources[].source_type': [
    'SALARY',
    'BONUS',
    'COMMISSION',
    'SELF_EMPLOYMENT',
    'BUSINESS_DISTRIBUTION',
    'RENTAL',
    'DIVIDENDS',
    'INTEREST',
    'SOCIAL_SECURITY',
    'PENSION',
    'ANNUITY',
    'ALIMONY',
    'CUSTOM',
    'OTHER',
  ],
  'sections.income_sources[].frequency': [
    'ANNUAL',
    'MONTHLY',
    'QUARTERLY',
    'BI_WEEKLY',
    'WEEKLY',
    'ONE_TIME',
  ],
  'sections.income_sources[].amount_tax_basis': ['GROSS', 'NET'],
  'sections.income_sources[].start_timing': [
    'ALREADY_STARTED',
    'SPECIFIC_MONTH',
    'FRA',
    'CLAIMING_AGE',
  ],
  'sections.income_sources[].end_timing': ['ONGOING', 'SPECIFIC_MONTH'],
  'sections.expenses[].frequency': [
    'ANNUAL',
    'MONTHLY',
    'QUARTERLY',
    'BI_WEEKLY',
    'WEEKLY',
    'ONE_TIME',
  ],
  'sections.accounts[].account_type': [
    'CHECKING',
    'SAVINGS',
    'MONEY_MARKET',
    'CD',
    'TAXABLE_BROKERAGE',
    'TRADITIONAL_IRA',
    'ROTH_IRA',
    '401K',
    'ROTH_401K',
    '403B',
    '457',
    'SEP_IRA',
    'SIMPLE_IRA',
    'HSA',
    '529',
    'PENSION',
    'ANNUITY',
    'OTHER',
  ],
  'sections.properties[].property_type': [
    'PRIMARY_RESIDENCE',
    'VACATION_HOME',
    'RENTAL',
    'LAND',
    'COMMERCIAL',
    'OTHER',
  ],
  'sections.debts[].debt_type': [
    'MORTGAGE',
    'HELOC',
    'STUDENT_LOAN_FEDERAL',
    'STUDENT_LOAN_PRIVATE',
    'AUTO_LOAN',
    'CREDIT_CARD',
    'PERSONAL_LOAN',
    'BUSINESS_LOAN',
    'MARGIN_LOAN',
    '401K_LOAN',
    'FAMILY_LOAN',
    'MEDICAL',
    'TAX',
    'OTHER',
  ],
  'sections.tax.filing_status': [
    'SINGLE',
    'MARRIED_FILING_JOINTLY',
    'MARRIED_FILING_SEPARATELY',
    'HEAD_OF_HOUSEHOLD',
    'QUALIFYING_WIDOW',
  ],
  'sections.tax.itemize_or_standard': ['ITEMIZE', 'STANDARD', 'UNKNOWN'],
  'sections.tax.anticipated_events': [
    'RSU_VEST',
    'ISO_EXERCISE',
    'BUSINESS_SALE',
    'ROTH_CONVERSION',
    'LARGE_GIFT',
    'HOME_SALE',
    'INHERITANCE',
    'NONE',
  ],
  'sections.goals[].category': [
    'RETIREMENT',
    'EDUCATION',
    'HOME_PURCHASE',
    'BUSINESS',
    'TRAVEL',
    'LEGACY',
    'PHILANTHROPY',
    'EMERGENCY_FUND',
    'OTHER',
  ],
  'sections.goals[].priority': ['MUST_HAVE', 'WANT', 'WISH'],
  'sections.beneficiary_candidates[].source_status': [
    'ADVISOR_ENTERED',
    'INVESTOR_STATED',
    'DOCUMENT_REVIEWED',
    'INSTITUTION_CONFIRMED',
    'UNKNOWN',
  ],
  'sections.risk.tolerance': [
    'CONSERVATIVE',
    'MODERATE_CONSERVATIVE',
    'MODERATE',
    'MODERATE_AGGRESSIVE',
    'AGGRESSIVE',
  ],
};

const SPECIAL_OPTION_LABELS: Record<string, string> = {
  '401K': '401(k)',
  '401K_LOAN': '401(k) loan',
  '403B': '403(b)',
  '457': '457',
  '529': '529',
  CD: 'CD',
  DOMESTIC_PARTNERSHIP: 'Domestic partnership',
  MARITAL_STATUS_UNSPECIFIED: 'Unspecified',
  US_CITIZEN: 'US citizen',
  PERMANENT_RESIDENT: 'Permanent resident',
  NON_RESIDENT_ALIEN: 'Non-resident alien',
  DUAL_CITIZEN: 'Dual citizen',
  HSA: 'HSA',
  HELOC: 'HELOC',
  EMPLOYMENT_STATUS_UNSPECIFIED: 'Unspecified',
  SELF_EMPLOYED: 'Self-employed',
  BUSINESS_OWNER: 'Business owner',
  NOT_WORKING: 'Not working',
  ADOPTED_CHILD: 'Adopted child',
  OTHER_DEPENDENT: 'Other dependent',
  SELF_EMPLOYMENT: 'Self-employment',
  BUSINESS_DISTRIBUTION: 'Business distribution',
  SOCIAL_SECURITY: 'Social Security',
  CUSTOM: 'Custom',
  GROSS: 'Gross',
  NET: 'Net',
  ALREADY_STARTED: 'Already started',
  SPECIFIC_MONTH: 'Specific month',
  FRA: 'Full retirement age',
  CLAIMING_AGE: 'Claiming age',
  ONGOING: 'Ongoing',
  BI_WEEKLY: 'Biweekly',
  ONE_TIME: 'One-time',
  MONEY_MARKET: 'Money market',
  TAXABLE_BROKERAGE: 'Taxable brokerage',
  TRADITIONAL_IRA: 'Traditional IRA',
  ROTH_IRA: 'Roth IRA',
  ROTH_401K: 'Roth 401(k)',
  SEP_IRA: 'SEP IRA',
  SIMPLE_IRA: 'Simple IRA',
  VACATION_HOME: 'Vacation home',
  PRIMARY_RESIDENCE: 'Primary residence',
  STUDENT_LOAN_FEDERAL: 'Federal student loan',
  STUDENT_LOAN_PRIVATE: 'Private student loan',
  AUTO_LOAN: 'Auto loan',
  CREDIT_CARD: 'Credit card',
  PERSONAL_LOAN: 'Personal loan',
  BUSINESS_LOAN: 'Business loan',
  MARGIN_LOAN: 'Margin loan',
  FAMILY_LOAN: 'Family loan',
  MARRIED_FILING_JOINTLY: 'Married filing jointly',
  MARRIED_FILING_SEPARATELY: 'Married filing separately',
  HEAD_OF_HOUSEHOLD: 'Head of household',
  QUALIFYING_WIDOW: 'Qualifying widow',
  ISO_EXERCISE: 'ISO exercise',
  RSU_VEST: 'RSU vest',
  BUSINESS_SALE: 'Business sale',
  ROTH_CONVERSION: 'Roth conversion',
  LARGE_GIFT: 'Large gift',
  HOME_SALE: 'Home sale',
  INHERITANCE: 'Inheritance',
  NONE: 'None',
  HOME_PURCHASE: 'Home purchase',
  EMERGENCY_FUND: 'Emergency fund',
  MUST_HAVE: 'Must have',
  PRIMARY: 'Primary',
  CONTINGENT: 'Contingent',
  SUCCESSOR_TRUSTEE: 'Successor trustee',
  BUSINESS_SUCCESSOR: 'Business successor',
  ADVISOR_ENTERED: 'Advisor-entered',
  INVESTOR_STATED: 'Investor-stated',
  DOCUMENT_REVIEWED: 'Document-reviewed',
  INSTITUTION_CONFIRMED: 'Institution-confirmed',
  MODERATE_CONSERVATIVE: 'Moderate conservative',
  MODERATE_AGGRESSIVE: 'Moderate aggressive',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const isScalar = (value: unknown): value is string | number | boolean | null =>
  value === null || ['string', 'number', 'boolean'].includes(typeof value);

const NAME_PART_KEYS = ['given_name', 'middle_names', 'family_name', 'name_suffix'] as const;

const concretePathToMetadataPath = (path: string): string => path.replace(/\.(\d+)(?=\.|$)/g, '[]');

const metadataForPath = (
  metadata: Record<string, unknown>,
  path: string,
  fallback: Record<string, unknown> = {},
): Record<string, unknown> => {
  const exact = metadata[path];
  if (isRecord(exact)) return exact;

  const wildcard = metadata[concretePathToMetadataPath(path)];
  if (isRecord(wildcard)) return wildcard;

  return fallback;
};

const getValueAtPath = (payload: InvestorProfilePayload, path: string): unknown => {
  let current: unknown = payload;
  for (const segment of path.split('.')) {
    if (Array.isArray(current) && /^\d+$/.test(segment)) {
      current = current[Number(segment)];
      continue;
    }
    if (isRecord(current) && Object.prototype.hasOwnProperty.call(current, segment)) {
      current = current[segment];
      continue;
    }
    return undefined;
  }
  return current;
};

const getValidationFormat = (metadata: Record<string, unknown>): string | null => {
  const validation = metadata.validation;
  if (!isRecord(validation)) return null;
  return typeof validation.format === 'string' ? validation.format : null;
};

const hasValidationFormat = (metadata: Record<string, unknown>, format: string): boolean =>
  getValidationFormat(metadata) === format;

const hasRepeatableChildMetadata = (metadata: Record<string, unknown>, path: string): boolean =>
  Object.keys(metadata).some((metadataPath) => metadataPath.startsWith(`${path}[].`));

const sentenceize = (value: string): string => {
  const words = value.split('_').map((word, index) => {
    if (/^[A-Z0-9]{2,4}$/.test(word)) return word;
    const lower = word.toLowerCase();
    return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
  });
  return words.join(' ');
};

const optionLabel = (value: string): string => SPECIAL_OPTION_LABELS[value] ?? sentenceize(value);

const fieldOptionsFallbackPath = (path: string): string => path.replace(/\.\d+(?=\.|$)/g, '[]');

const enumValuesFromMetadata = (metadata: Record<string, unknown>): string[] | undefined => {
  const validation = metadata.validation;
  if (!isRecord(validation) || !Array.isArray(validation.enum_values)) return undefined;

  const values = [
    ...new Set(
      validation.enum_values.filter(
        (value): value is string => typeof value === 'string' && value.trim().length > 0,
      ),
    ),
  ];
  return values.length > 0 ? values : undefined;
};

const enumOptionsForField = (
  path: string,
  metadata: Record<string, unknown>,
): ProfilePayloadInputOption[] | undefined => {
  const values =
    enumValuesFromMetadata(metadata) ??
    PROFILE_ENUM_VALUE_FALLBACKS[path] ??
    PROFILE_ENUM_VALUE_FALLBACKS[fieldOptionsFallbackPath(path)];
  if (!values) return undefined;

  return values.map((value) => ({
    value,
    label: optionLabel(value),
  }));
};

const singularSectionLabel = (segment: string): string =>
  (
    ({
      dependents: 'Dependent',
      income_sources: 'Income source',
      expenses: 'Expense',
      accounts: 'Account',
      properties: 'Property',
      businesses: 'Business',
      beneficiary_candidates: 'Beneficiary candidate',
      debts: 'Debt',
      goals: 'Goal',
    }) as Record<string, string>
  )[segment] ?? sentenceize(segment);

const fieldLabel = (segment: string): string =>
  (
    ({
      amount: 'Amount',
      annual_gross_income: 'Annual gross income',
      annual_one_time: 'Annual one-time expenses',
      birth_date: 'Birth date',
      cost_basis: 'Cost basis',
      current_balance: 'Current balance',
      current_value: 'Current value',
      citizenship_country_primary: 'Citizenship country',
      citizenship_country_secondary: 'Second citizenship country',
      date_of_birth: 'Date of birth',
      debt_type: 'Debt type',
      desired_annual_spending: 'Desired retirement annual spending',
      end_date: 'End date',
      editable_from_profile: 'Editable from profile',
      employment_status: 'Employment status',
      entity_name: 'Entity name',
      entity_type: 'Entity type',
      employer_name: 'Employer name',
      estimated_value: 'Estimated value',
      ended_on: 'Ended on',
      end_month: 'End month',
      end_timing: 'End timing',
      federal_marginal_bracket: 'Federal marginal tax bracket',
      given_name: 'First name',
      gross_annual_amount: 'Gross annual amount',
      inflation_rate: 'Inflation rate',
      itemize_or_standard: 'Deduction type',
      job_title: 'Job title',
      joint_with_spouse: 'Joint with spouse',
      family_name: 'Last name',
      last_verified_date: 'Last verified date',
      line1: 'Address line 1',
      line2: 'Address line 2',
      line3: 'Address line 3',
      mailing_address: 'Mailing address',
      middle_names: 'Middle names',
      mortgage_balance: 'Mortgage balance',
      name_suffix: 'Suffix',
      owns_business: 'Owns business',
      payoff_date: 'Payoff date',
      postal_code: 'Postal code',
      primary_beneficiary: 'Primary beneficiary',
      primary_residence_address: 'Primary residence address',
      primary_residence_state: 'State of domicile',
      property_type: 'Property type',
      region: 'State',
      relation_type: 'Relationship',
      shared_with_spouse: 'Shared with spouse',
      source_status: 'Source/status',
      source_type: 'Income source type',
      spouse_target_age: 'Spouse target retirement age',
      start_date: 'Start date',
      started_on: 'Started on',
      start_month: 'Start month',
      start_timing: 'Start timing',
      state_marginal_bracket: 'State marginal tax bracket',
      tax_id: 'Tax ID',
      tax_id_type: 'Tax ID type',
      target_age: 'Target retirement age',
      target_amount: 'Target amount',
      target_date: 'Target date',
      total_fixed_monthly: 'Fixed monthly expenses',
      total_variable_monthly: 'Variable monthly expenses',
      amount_tax_basis: 'Tax basis',
      claiming_age: 'Claiming age',
      cola_enabled: 'COLA',
      cola_percent: 'COLA percent',
      custom_source_type: 'Custom source type',
      is_advisor: 'Financial advisor',
      is_institutional_customer: 'Institutional customer',
      is_correspondent_employee: 'Apex/correspondent employee',
      is_finra_affiliated: 'FINRA affiliated',
      finra_associated_entity: 'FINRA associated entity',
      is_control_person: 'Control person',
      control_person_company_symbols: 'Control-person company symbols',
      has_politically_exposed_family: 'Politically exposed family',
      politically_exposed_family_names: 'Politically exposed family names',
      has_politically_exposed_organization: 'Politically exposed organization',
      politically_exposed_organization: 'Politically exposed organization name',
    }) as Record<string, string>
  )[segment] ?? sentenceize(segment);

const namePartLabelForPath = (path: string): string | null => {
  const match = path.match(/^(.*)\.name\.(given_name|middle_names|family_name|name_suffix)$/);
  if (!match) return null;

  const partLabel = (
    {
      given_name: 'First name',
      middle_names: 'Middle names',
      family_name: 'Last name',
      name_suffix: 'Suffix',
    } as Record<string, string>
  )[match[2]];

  if (match[1] === 'sections.investor' || match[1] === 'sections.spouse') return partLabel;

  const dependentMatch = match[1].match(/^sections\.dependents\.(\d+)$/);
  if (dependentMatch)
    return `Dependent ${Number(dependentMatch[1]) + 1} ${partLabel.toLowerCase()}`;

  return partLabel;
};

const isPersonNamePath = (path: string): boolean =>
  path === 'sections.investor.name' ||
  path === 'sections.spouse.name' ||
  /^sections\.(dependents|beneficiary_candidates)\.\d+\.name$/.test(path);

const repeatableFieldLabel = (path: string): string | null => {
  if (/^sections\.income_sources\.\d+\.name$/.test(path)) return 'Name';
  if (/^sections\.income_sources\.\d+\.(gross_annual_amount|amount)$/.test(path)) {
    return 'Gross income amount';
  }
  if (/^sections\.expenses\.\d+\.name$/.test(path)) return 'Name';
  if (/^sections\.expenses\.\d+\.amount$/.test(path)) return 'Amount';
  return null;
};

const labelForPath = (path: string): string => {
  if (path === 'sections.spouse.annual_gross_income') return 'Gross income amount';
  const repeatableLabel = repeatableFieldLabel(path);
  if (repeatableLabel) return repeatableLabel;
  const nameLabel = namePartLabelForPath(path);
  if (nameLabel) return nameLabel;
  if (SPECIAL_LABELS[path]) return SPECIAL_LABELS[path];
  if (path.endsWith('.phone_number.e164_number')) {
    return `${labelForPath(path.slice(0, -'.phone_number.e164_number'.length))} / Phone number`;
  }

  const labels: string[] = [];
  const segments = path.replace(/^sections\./, '').split('.');
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    const nextSegment = segments[index + 1];
    if (/^\d+$/.test(segment)) continue;
    if (nextSegment && /^\d+$/.test(nextSegment)) {
      labels.push(`${singularSectionLabel(segment)} ${Number(nextSegment) + 1}`);
      index += 1;
      continue;
    }
    if (segment === 'amount' || segment === 'currency' || segment === 'percent') continue;
    labels.push(fieldLabel(segment));
  }
  return labels.join(' / ');
};

const inputKindForField = (
  path: string,
  value: string | number | boolean | null,
  metadata: Record<string, unknown>,
): ProfilePayloadInputKind => {
  if (SPECIAL_INPUT_KINDS[path]) return SPECIAL_INPUT_KINDS[path];
  if (BOOLEAN_FIELD_SUFFIXES.some((suffix) => path.endsWith(suffix))) return 'boolean';
  if (/\.birth_date\.(month|day|year)$/.test(path)) return 'number';
  if (path.endsWith('.email')) return 'email';
  if (path.endsWith('.phone_number.e164_number')) return 'tel';

  const format = getValidationFormat(metadata);
  if (format === 'boolean') return 'boolean';
  if (format === 'date') return 'date';
  if (format === 'month') return 'month';
  if (format === 'email') return 'email';
  if (format === 'phone') return 'tel';
  if (format === 'money') return 'number';
  if (format === 'percent') return 'number';
  if (format === 'non_negative_integer') return 'number';

  if (
    path.endsWith('.date_of_birth') ||
    path.endsWith('.started_on') ||
    path.endsWith('.ended_on') ||
    path.endsWith('.start_date') ||
    path.endsWith('.end_date') ||
    path.endsWith('.target_date') ||
    path.endsWith('.last_verified_date') ||
    path.endsWith('.payoff_date')
  ) {
    return 'date';
  }

  if (path.endsWith('.start_month') || path.endsWith('.end_month')) {
    return 'month';
  }

  if (typeof value === 'number') return 'number';
  if (typeof value === 'boolean') return 'boolean';
  return 'text';
};

const nameDisplayValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (!isRecord(value)) return null;

  if (typeof value.display_name === 'string' && value.display_name.trim()) {
    return value.display_name.trim();
  }

  const parts = ['given_name', 'middle_names', 'family_name', 'name_suffix']
    .map((key) => value[key])
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .map((part) => part.trim());

  return parts.length > 0 ? parts.join(' ') : null;
};

const formValueFromScalar = (value: string | number | boolean | null): string => {
  if (value === null) return '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return String(value);
};

const stringValue = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const splitDisplayName = (
  value: string | null,
): Record<(typeof NAME_PART_KEYS)[number], string | null> => {
  const parts = value?.split(/\s+/).filter(Boolean) ?? [];
  if (parts.length === 0) {
    return { given_name: null, middle_names: null, family_name: null, name_suffix: null };
  }
  if (parts.length === 1) {
    return { given_name: parts[0], middle_names: null, family_name: null, name_suffix: null };
  }
  if (parts.length === 2) {
    return {
      given_name: parts[0],
      middle_names: null,
      family_name: parts[1],
      name_suffix: null,
    };
  }
  return {
    given_name: parts[0],
    middle_names: parts.slice(1, -1).join(' '),
    family_name: parts[parts.length - 1],
    name_suffix: null,
  };
};

const namePartsFromValue = (
  value: unknown,
): Record<(typeof NAME_PART_KEYS)[number], string | null> => {
  if (!isRecord(value)) return splitDisplayName(nameDisplayValue(value));

  const explicitParts = {
    given_name: stringValue(value.given_name),
    middle_names: stringValue(value.middle_names),
    family_name: stringValue(value.family_name),
    name_suffix: stringValue(value.name_suffix),
  };
  if (Object.values(explicitParts).some((part) => part !== null)) return explicitParts;

  return splitDisplayName(nameDisplayValue(value));
};

const isMoneyValue = (value: unknown): value is Record<string, unknown> => {
  if (!isRecord(value)) return false;
  if (typeof value.currency === 'string') return true;

  const amount = value.amount;
  return (
    Object.prototype.hasOwnProperty.call(value, 'amount') &&
    (amount === null || typeof amount === 'string' || typeof amount === 'number')
  );
};

const moneyAmountValue = (value: unknown): string | null => {
  if (!isMoneyValue(value)) return null;
  const amount = value.amount;
  if (typeof amount === 'string') {
    const trimmed = amount.trim();
    return trimmed || null;
  }
  if (typeof amount === 'number' && Number.isFinite(amount)) return String(amount);
  return null;
};

const moneyCurrencyValue = (value: unknown): string =>
  isMoneyValue(value) && typeof value.currency === 'string' && value.currency.trim()
    ? value.currency.trim()
    : 'USD';

const isPercentValue = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) && Object.prototype.hasOwnProperty.call(value, 'percent');

const percentValue = (value: unknown): string | null => {
  if (!isPercentValue(value)) return null;
  const percent = value.percent;
  if (typeof percent === 'string') {
    const trimmed = percent.trim();
    return trimmed || null;
  }
  if (typeof percent === 'number' && Number.isFinite(percent)) return String(percent);
  return null;
};

const stringListFormValue = (value: unknown): string => {
  if (!Array.isArray(value)) return '';
  return value
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .map((item) => item.trim())
    .join(', ');
};

const stringListValuesFromFormValue = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const normalizedOriginalValue = (
  field: Omit<ProfilePayloadInputField, 'value' | 'originalValue'>,
  rawValue: unknown,
): ProfilePayloadOriginalValue => {
  if (isPersonNamePath(field.path)) return nameDisplayValue(rawValue);
  if (rawValue === undefined || rawValue === null) return null;

  if (field.valueKind === 'string-list') {
    if (!Array.isArray(rawValue)) return [];
    return rawValue
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map((item) => item.trim());
  }

  if (field.inputKind === 'number') {
    if (typeof rawValue === 'number') return rawValue;
    if (typeof rawValue === 'string' && rawValue.trim()) return Number(rawValue);
    return null;
  }

  if (field.inputKind === 'boolean') {
    return typeof rawValue === 'boolean' ? rawValue : null;
  }

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    return trimmed || null;
  }

  return isScalar(rawValue) ? rawValue : null;
};

const addNamePartFields = (
  fields: Map<string, ProfilePayloadInputField>,
  path: string,
  rawValue: unknown,
) => {
  const parts = namePartsFromValue(rawValue);
  for (const key of NAME_PART_KEYS) {
    const value = parts[key];
    fields.set(`${path}.${key}`, {
      path: `${path}.${key}`,
      label: labelForPath(`${path}.${key}`),
      inputKind: 'text',
      value: value ?? '',
      required: false,
      originalValue: value,
      valueKind: 'name-part',
      groupPath: path,
    });
  }
};

const addMoneyField = (
  fields: Map<string, ProfilePayloadInputField>,
  path: string,
  rawValue: unknown,
  metadata: Record<string, unknown>,
) => {
  const value = moneyAmountValue(rawValue);
  fields.set(path, {
    path,
    label: labelForPath(path),
    inputKind: 'number',
    value: value ?? '',
    required: metadata.required === true,
    originalValue: value,
    valueKind: 'money',
    currency: moneyCurrencyValue(rawValue),
  });
};

const addPercentField = (
  fields: Map<string, ProfilePayloadInputField>,
  path: string,
  rawValue: unknown,
  metadata: Record<string, unknown>,
) => {
  const value = percentValue(rawValue);
  fields.set(path, {
    path,
    label: labelForPath(path),
    inputKind: 'number',
    value: value ?? '',
    required: metadata.required === true,
    originalValue: value,
    valueKind: 'percent',
  });
};

const addStringListField = (
  fields: Map<string, ProfilePayloadInputField>,
  path: string,
  rawValue: unknown,
  metadata: Record<string, unknown>,
) => {
  const originalValue = normalizedOriginalValue(
    {
      path,
      label: labelForPath(path),
      inputKind: 'text',
      required: metadata.required === true,
      valueKind: 'string-list',
    },
    rawValue,
  );
  fields.set(path, {
    path,
    label: labelForPath(path),
    inputKind: 'text',
    value: stringListFormValue(rawValue),
    required: metadata.required === true,
    originalValue,
    valueKind: 'string-list',
    options: enumOptionsForField(path, metadata),
  });
};

const addField = (
  fields: Map<string, ProfilePayloadInputField>,
  path: string,
  rawValue: unknown,
  metadata: Record<string, unknown>,
) => {
  if (isPersonNamePath(path)) {
    addNamePartFields(fields, path, rawValue);
    return;
  }

  if (
    SPECIAL_MONEY_FIELD_PATHS.has(path) ||
    hasValidationFormat(metadata, 'money') ||
    isMoneyValue(rawValue)
  ) {
    addMoneyField(fields, path, rawValue, metadata);
    return;
  }

  if (hasValidationFormat(metadata, 'percent') || isPercentValue(rawValue)) {
    addPercentField(fields, path, rawValue, metadata);
    return;
  }

  if (Array.isArray(rawValue) && rawValue.every((item) => typeof item === 'string')) {
    addStringListField(fields, path, rawValue, metadata);
    return;
  }

  const scalarValue = isScalar(rawValue) ? rawValue : null;
  const fieldBase = {
    path,
    label: labelForPath(path),
    inputKind: inputKindForField(path, scalarValue, metadata),
    required: metadata.required === true,
    valueKind: 'scalar' as const,
    options: enumOptionsForField(path, metadata),
  };
  const originalValue = normalizedOriginalValue(fieldBase, scalarValue);
  fields.set(path, {
    ...fieldBase,
    value: formValueFromScalar(originalValue as string | number | boolean | null),
    originalValue,
  });
};

const addScalarChildren = (
  fields: Map<string, ProfilePayloadInputField>,
  path: string,
  value: Record<string, unknown> | unknown[],
  allMetadata: Record<string, unknown>,
  metadata: Record<string, unknown>,
) => {
  if (Array.isArray(value)) {
    if (value.length === 0 && hasRepeatableChildMetadata(allMetadata, path)) {
      return;
    }

    if (value.every((item) => typeof item === 'string')) {
      addField(fields, path, value, metadata);
      return;
    }

    value.forEach((childValue, index) => {
      const childPath = `${path}.${index}`;
      const childMetadata = metadataForPath(allMetadata, childPath, metadata);
      if (isRecord(childMetadata) && childMetadata.editable === false) return;

      if (isRecord(childValue)) {
        addScalarChildren(fields, childPath, childValue, allMetadata, childMetadata);
        return;
      }

      if (isScalar(childValue) || childValue === undefined) {
        addField(fields, childPath, childValue ?? null, { ...childMetadata, required: false });
      }
    });
    return;
  }

  if (isPersonNamePath(path) || isMoneyValue(value) || isPercentValue(value)) {
    addField(fields, path, value, metadata);
    return;
  }

  for (const [key, childValue] of Object.entries(value)) {
    const childPath = `${path}.${key}`;
    const childMetadata = metadataForPath(allMetadata, childPath, metadata);
    if (childMetadata.editable === false) continue;

    if (Array.isArray(childValue)) {
      addScalarChildren(fields, childPath, childValue, allMetadata, childMetadata);
      continue;
    }

    if (isRecord(childValue)) {
      if (isMoneyValue(childValue)) {
        addField(fields, childPath, childValue, { ...childMetadata, required: false });
        continue;
      }
      if (isPercentValue(childValue)) {
        addField(fields, childPath, childValue, { ...childMetadata, required: false });
        continue;
      }
      addScalarChildren(fields, childPath, childValue, allMetadata, childMetadata);
      continue;
    }

    if (isScalar(childValue) || childValue === undefined) {
      addField(fields, childPath, childValue ?? null, { ...childMetadata, required: false });
    }
  }
};

export const getProfilePayloadRevision = (payload: InvestorProfilePayload): string =>
  typeof payload.revision === 'string' ? payload.revision : '';

export const getEditableProfilePayloadFields = (
  payload: InvestorProfilePayload,
): ProfilePayloadInputField[] => {
  const metadata = isRecord(payload.field_metadata) ? payload.field_metadata : {};
  const fields = new Map<string, ProfilePayloadInputField>();

  for (const [path, rawMetadata] of Object.entries(metadata).sort(([left], [right]) =>
    left.localeCompare(right),
  )) {
    if (path.includes('[]')) continue;
    if (!isRecord(rawMetadata) || rawMetadata.editable !== true) continue;

    const rawValue = getValueAtPath(payload, path);
    const pathSegments = path.split('.');

    if (path.endsWith('.name')) {
      addField(fields, path, rawValue, rawMetadata);
      continue;
    }

    if (Array.isArray(rawValue)) {
      addScalarChildren(fields, path, rawValue, metadata, rawMetadata);
      continue;
    }

    if (isScalar(rawValue) || (rawValue === undefined && pathSegments.length > 2)) {
      addField(fields, path, rawValue ?? null, rawMetadata);
      continue;
    }

    if (isRecord(rawValue)) {
      addScalarChildren(fields, path, rawValue, metadata, rawMetadata);
    }
  }

  return [...fields.values()].sort((left, right) => left.path.localeCompare(right.path));
};

const editableProfilePayloadFieldsExcluding = (
  payload: InvestorProfilePayload,
  disabledFieldPrefixes: readonly string[],
): ProfilePayloadInputField[] => {
  const fields = getEditableProfilePayloadFields(payload);
  if (disabledFieldPrefixes.length === 0) return fields;

  return fields.filter(
    (field) =>
      !disabledFieldPrefixes.some(
        (prefix) => field.path === prefix || field.path.startsWith(`${prefix}.`),
      ),
  );
};

export const defaultProfilePayloadFormValues = (
  payload: InvestorProfilePayload,
): ProfilePayloadFormValues =>
  Object.fromEntries(
    getEditableProfilePayloadFields(payload).map((field) => [field.path, field.value]),
  );

const DECIMAL_VALUE_PATTERN = /^-?\d+(\.\d+)?$/;
const INCOME_SOURCE_SOCIAL_SECURITY_TYPE = 'SOCIAL_SECURITY';
const SOCIAL_SECURITY_START_TIMINGS = new Set(['FRA', 'CLAIMING_AGE']);
const WORKING_EMPLOYMENT_STATUSES = new Set(['EMPLOYED', 'SELF_EMPLOYED', 'BUSINESS_OWNER']);
const NON_WORKING_EMPLOYMENT_STATUSES = new Set([
  'RETIRED',
  'NOT_WORKING',
  'UNEMPLOYED',
  'STUDENT',
  'HOMEMAKER',
]);
const MARRIED_FILING_STATUSES = new Set(['MARRIED_FILING_JOINTLY', 'MARRIED_FILING_SEPARATELY']);
const SPOUSE_COMPATIBLE_MARITAL_STATUSES = new Set(['MARRIED', 'DOMESTIC_PARTNERSHIP']);
const SPOUSE_INCOMPATIBLE_MARITAL_STATUSES = new Set(['SINGLE', 'DIVORCED', 'WIDOWED']);

type RepeatableCompanionRequirement = {
  rootPath: string;
  triggerSuffixes: readonly string[];
  requiredSuffixGroups: readonly (readonly string[])[];
};

const REPEATABLE_COMPANION_REQUIREMENTS: RepeatableCompanionRequirement[] = [
  {
    rootPath: 'sections.dependents',
    triggerSuffixes: [
      'name.given_name',
      'name.middle_names',
      'name.family_name',
      'name.name_suffix',
      'date_of_birth',
      'relationship',
    ],
    requiredSuffixGroups: [['relationship']],
  },
  {
    rootPath: 'sections.income_sources',
    triggerSuffixes: [
      'name',
      'source_type',
      'gross_annual_amount',
      'amount',
      'frequency',
      'joint_with_spouse',
    ],
    requiredSuffixGroups: [['source_type'], ['gross_annual_amount', 'amount']],
  },
  {
    rootPath: 'sections.expenses',
    triggerSuffixes: ['name', 'amount', 'frequency', 'start_date', 'end_date', 'inflation_rate'],
    requiredSuffixGroups: [['name'], ['amount']],
  },
  {
    rootPath: 'sections.business.businesses',
    triggerSuffixes: ['name', 'estimated_value', 'joint_with_spouse'],
    requiredSuffixGroups: [['name']],
  },
  {
    rootPath: 'sections.businesses',
    triggerSuffixes: ['name', 'estimated_value', 'joint_with_spouse'],
    requiredSuffixGroups: [['name']],
  },
  {
    rootPath: 'sections.goals',
    triggerSuffixes: ['category', 'target_amount', 'target_date', 'priority'],
    requiredSuffixGroups: [['category'], ['target_amount']],
  },
];

const normalizedDecimalFormValue = (value: string): string => value.replace(/[$,]/g, '');

const decimalNumberFromFormValue = (value: string): number | null => {
  const decimalValue = normalizedDecimalFormValue(value.trim());
  if (!DECIMAL_VALUE_PATTERN.test(decimalValue)) return null;
  const numericValue = Number(decimalValue);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const ANNUALIZATION_FACTORS: Record<string, number> = {
  ANNUAL: 1,
  MONTHLY: 12,
  QUARTERLY: 4,
  BI_WEEKLY: 26,
  WEEKLY: 52,
  ONE_TIME: 1,
};

const annualizationFactor = (frequency: string): number => ANNUALIZATION_FACTORS[frequency] ?? 1;

const parseIsoDateParts = (
  value: string,
): { year: number; month: number; day: number; date: Date } | null => {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const dateValue = new Date(Date.UTC(year, month - 1, day));
  if (
    dateValue.getUTCFullYear() !== year ||
    dateValue.getUTCMonth() !== month - 1 ||
    dateValue.getUTCDate() !== day
  ) {
    return null;
  }

  return { year, month, day, date: dateValue };
};

const parseYearMonthParts = (value: string): { year: number; month: number } | null => {
  const match = value.trim().match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }

  return { year, month };
};

const todayUtcDate = (): Date => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};

const ageOnDate = (birthDate: Date, asOfDate: Date): number => {
  let age = asOfDate.getUTCFullYear() - birthDate.getUTCFullYear();
  const beforeBirthday =
    asOfDate.getUTCMonth() < birthDate.getUTCMonth() ||
    (asOfDate.getUTCMonth() === birthDate.getUTCMonth() &&
      asOfDate.getUTCDate() < birthDate.getUTCDate());
  if (beforeBirthday) age -= 1;
  return age;
};

const hasFormValue = (value: string | undefined): boolean => Boolean(value?.trim());

const isDateInputField = (field: ProfilePayloadInputField): boolean =>
  field.inputKind === 'date' ||
  field.path.endsWith('.date_of_birth') ||
  field.path.endsWith('.started_on') ||
  field.path.endsWith('.ended_on') ||
  field.path.endsWith('.start_date') ||
  field.path.endsWith('.end_date') ||
  field.path.endsWith('.target_date') ||
  field.path.endsWith('.last_verified_date') ||
  field.path.endsWith('.payoff_date');

const isMonthInputField = (field: ProfilePayloadInputField): boolean =>
  field.inputKind === 'month' ||
  field.path.endsWith('.start_month') ||
  field.path.endsWith('.end_month');

const isRetirementAgeField = (path: string): boolean =>
  path === 'sections.retirement.target_age' || path === 'sections.retirement.spouse_target_age';

const isSocialSecurityClaimingAgeField = (path: string): boolean =>
  /^sections\.income_sources\.\d+\.claiming_age$/.test(path);

const validationResultFromMessages = (
  messages: ProfilePayloadFieldValidationMessage[],
): ProfilePayloadValidationResult => {
  const messagesByPath: Record<string, ProfilePayloadFieldValidationMessage[]> = {};
  for (const message of messages) {
    messagesByPath[message.fieldPath] = [...(messagesByPath[message.fieldPath] ?? []), message];
  }

  return {
    messages,
    messagesByPath,
    hasErrors: messages.some((message) => message.severity === 'error'),
    hasWarnings: messages.some((message) => message.severity === 'warning'),
    firstErrorPath: messages.find((message) => message.severity === 'error')?.fieldPath ?? null,
  };
};

const profilePayloadValidationErrorMessage = (
  result: ProfilePayloadValidationResult,
  fields: ProfilePayloadInputField[],
): string | null => {
  const firstError = result.messages.find((message) => message.severity === 'error');
  if (!firstError) return null;
  const field = fields.find((candidate) => candidate.path === firstError.fieldPath);
  return field ? `${field.label}: ${firstError.message}` : firstError.message;
};

const addValidationMessage = (
  messages: ProfilePayloadFieldValidationMessage[],
  seen: Set<string>,
  message: ProfilePayloadFieldValidationMessage,
) => {
  const key = `${message.fieldPath}|${message.severity}|${message.message}`;
  if (seen.has(key)) return;
  seen.add(key);
  messages.push(message);
};

const validateSingleProfileField = (
  field: ProfilePayloadInputField,
  rawValue: string | undefined,
): ProfilePayloadFieldValidationMessage[] => {
  const value = (rawValue ?? '').trim();
  const messages: ProfilePayloadFieldValidationMessage[] = [];

  if (field.required && !value) {
    messages.push({
      fieldPath: field.path,
      severity: 'error',
      message: `${field.label} is required.`,
    });
    return messages;
  }
  if (!value) return messages;

  if (isSocialSecurityClaimingAgeField(field.path)) {
    const numericValue = Number(value);
    if (
      !Number.isFinite(numericValue) ||
      !Number.isInteger(numericValue) ||
      numericValue < 62 ||
      numericValue > 70
    ) {
      messages.push({
        fieldPath: field.path,
        severity: 'error',
        message: 'Claiming age must be a whole number from 62 to 70.',
      });
    }
    return messages;
  }

  if (field.valueKind === 'money') {
    const numericValue = decimalNumberFromFormValue(value);
    if (numericValue === null) {
      messages.push({
        fieldPath: field.path,
        severity: 'error',
        message: 'Enter a valid amount.',
      });
    } else if (numericValue < 0) {
      messages.push({
        fieldPath: field.path,
        severity: 'error',
        message: 'Amount must be zero or higher.',
      });
    }
    return messages;
  }

  if (field.valueKind === 'percent') {
    const numericValue = decimalNumberFromFormValue(value);
    if (numericValue === null) {
      messages.push({
        fieldPath: field.path,
        severity: 'error',
        message: 'Enter a valid percentage.',
      });
    } else if (numericValue < 0 || numericValue > 100) {
      messages.push({
        fieldPath: field.path,
        severity: 'error',
        message: 'Percentage must be between 0 and 100.',
      });
    }
    return messages;
  }

  if (field.valueKind === 'string-list') {
    if (field.options?.length) {
      const values = stringListValuesFromFormValue(value);
      const optionValues = new Set(field.options.map((option) => option.value));
      if (values.some((item) => !optionValues.has(item))) {
        messages.push({
          fieldPath: field.path,
          severity: 'error',
          message: 'Choose one of the available options.',
        });
      }
      if (
        NONE_EXCLUSIVE_STRING_LIST_FIELD_PATHS.has(field.path) &&
        values.includes('NONE') &&
        values.length > 1
      ) {
        messages.push({
          fieldPath: field.path,
          severity: 'error',
          message: 'Choose either none or specific anticipated events.',
        });
      }
    }
    return messages;
  }

  if (field.inputKind === 'number') {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      messages.push({
        fieldPath: field.path,
        severity: 'error',
        message: 'Enter a valid number.',
      });
      return messages;
    }
    if (isRetirementAgeField(field.path) && (!Number.isInteger(numericValue) || numericValue < 0)) {
      messages.push({
        fieldPath: field.path,
        severity: 'error',
        message: 'Retirement age must be a whole number zero or higher.',
      });
    }
  }

  if (isDateInputField(field) && parseIsoDateParts(value) === null) {
    messages.push({
      fieldPath: field.path,
      severity: 'error',
      message: 'Enter a valid date.',
    });
  }

  if (isMonthInputField(field) && parseYearMonthParts(value) === null) {
    messages.push({
      fieldPath: field.path,
      severity: 'error',
      message: 'Enter a valid month.',
    });
  }

  if (field.inputKind === 'boolean' && !['true', 'false'].includes(value)) {
    messages.push({
      fieldPath: field.path,
      severity: 'error',
      message: 'Choose true or false.',
    });
  }

  if (field.options?.length && !field.options.some((option) => option.value === value)) {
    messages.push({
      fieldPath: field.path,
      severity: 'error',
      message: 'Choose one of the available options.',
    });
  }

  return messages;
};

export const validateProfilePayloadForm = ({
  payload,
  values,
  disabledFieldPrefixes = [],
  repeatableCompanionValidationFieldPaths,
}: {
  payload: InvestorProfilePayload;
  values: ProfilePayloadFormValues;
  disabledFieldPrefixes?: readonly string[];
  repeatableCompanionValidationFieldPaths?: readonly string[];
}): ProfilePayloadValidationResult => {
  const fields = editableProfilePayloadFieldsExcluding(payload, disabledFieldPrefixes);
  const fieldByPath = new Map(fields.map((field) => [field.path, field]));
  const messages: ProfilePayloadFieldValidationMessage[] = [];
  const seenMessages = new Set<string>();
  const today = todayUtcDate();
  const repeatableCompanionValidationFieldPathSet =
    repeatableCompanionValidationFieldPaths === undefined
      ? null
      : new Set(repeatableCompanionValidationFieldPaths);

  const addMessage = (message: ProfilePayloadFieldValidationMessage) =>
    addValidationMessage(messages, seenMessages, message);
  const formValueForField = (field: ProfilePayloadInputField): string =>
    values[field.path] ?? field.value ?? '';

  const rawValueAt = (path: string): string =>
    (values[path] ?? fieldByPath.get(path)?.value ?? '').trim();
  const hasValueAt = (path: string): boolean =>
    fieldByPath.has(path) && hasFormValue(rawValueAt(path));
  const numberAt = (path: string): number | null => {
    if (!fieldByPath.has(path) || !hasValueAt(path)) return null;
    const numericValue = decimalNumberFromFormValue(rawValueAt(path));
    return numericValue !== null && numericValue >= 0 ? numericValue : null;
  };
  const monthAt = (path: string): { year: number; month: number } | null => {
    if (!fieldByPath.has(path) || !hasValueAt(path)) return null;
    return parseYearMonthParts(rawValueAt(path));
  };
  const monthSortValue = (value: { year: number; month: number }): number =>
    value.year * 12 + value.month;
  const dateAt = (path: string): Date | null => {
    if (!fieldByPath.has(path) || !hasValueAt(path)) return null;
    return parseIsoDateParts(rawValueAt(path))?.date ?? null;
  };
  const addMessageForExistingField = (
    path: string,
    severity: ProfilePayloadValidationSeverity,
    message: string,
    relatedFieldPaths?: string[],
  ) => {
    if (!fieldByPath.has(path)) return;
    addMessage({ fieldPath: path, severity, message, relatedFieldPaths });
  };
  const repeatableRowIndexesForRoot = (rootPath: string): number[] => {
    const prefix = `${rootPath}.`;
    const indexes = new Set<number>();
    for (const field of fields) {
      if (!field.path.startsWith(prefix)) continue;
      const segment = field.path.slice(prefix.length).split('.', 1)[0];
      if (/^\d+$/.test(segment)) indexes.add(Number(segment));
    }
    return [...indexes].sort((left, right) => left - right);
  };
  const rowFieldPath = (rootPath: string, rowIndex: number, suffix: string): string =>
    `${rootPath}.${rowIndex}.${suffix}`;
  const rowShouldValidateRepeatableCompanions = (rootPath: string, rowIndex: number): boolean => {
    if (repeatableCompanionValidationFieldPathSet === null) return true;
    const rowPrefix = `${rootPath}.${rowIndex}.`;
    for (const path of repeatableCompanionValidationFieldPathSet) {
      if (path.startsWith(rowPrefix)) return true;
    }
    return false;
  };
  const isInactiveIncomeMonthField = (path: string): boolean => {
    const startMatch = path.match(/^(sections\.income_sources\.\d+)\.start_month$/);
    if (startMatch) {
      return rawValueAt(`${startMatch[1]}.start_timing`) !== 'SPECIFIC_MONTH';
    }
    const endMatch = path.match(/^(sections\.income_sources\.\d+)\.end_month$/);
    if (endMatch) {
      return rawValueAt(`${endMatch[1]}.end_timing`) !== 'SPECIFIC_MONTH';
    }
    return false;
  };
  const shouldValidateSingleField = (field: ProfilePayloadInputField): boolean =>
    !isInactiveIncomeMonthField(field.path);
  const hasMeaningfulRowValueAt = (path: string): boolean => {
    const field = fieldByPath.get(path);
    if (!field) return false;
    const value = rawValueAt(path);
    if (!hasFormValue(value)) return false;
    return true;
  };

  for (const field of fields) {
    if (!shouldValidateSingleField(field)) continue;
    validateSingleProfileField(field, formValueForField(field)).forEach(addMessage);
  }
  const validateRepeatableCompanions = ({
    rootPath,
    triggerSuffixes,
    requiredSuffixGroups,
  }: RepeatableCompanionRequirement) => {
    for (const rowIndex of repeatableRowIndexesForRoot(rootPath)) {
      if (!rowShouldValidateRepeatableCompanions(rootPath, rowIndex)) continue;

      const triggerPaths = triggerSuffixes.map((suffix) =>
        rowFieldPath(rootPath, rowIndex, suffix),
      );
      if (!triggerPaths.some(hasMeaningfulRowValueAt)) continue;

      for (const suffixGroup of requiredSuffixGroups) {
        const requiredPaths = suffixGroup
          .map((suffix) => rowFieldPath(rootPath, rowIndex, suffix))
          .filter((path) => fieldByPath.has(path));
        if (requiredPaths.length === 0 || requiredPaths.some(hasValueAt)) continue;

        const missingPath = requiredPaths[0];
        const label = fieldByPath.get(missingPath)?.label ?? labelForPath(missingPath);
        addMessageForExistingField(
          missingPath,
          'error',
          `${label} is required when this row has other details.`,
          [...triggerPaths.filter((path) => fieldByPath.has(path)), ...requiredPaths],
        );
      }
    }
  };

  const validateEmploymentDates = (startPath: string, endPath: string) => {
    const startDate = dateAt(startPath);
    const endDate = dateAt(endPath);
    if (!startDate || !endDate || startDate.getTime() <= endDate.getTime()) return;
    const related = [startPath, endPath];
    addMessageForExistingField(
      startPath,
      'error',
      'Employment start date cannot be after the end date.',
      related,
    );
    addMessageForExistingField(
      endPath,
      'error',
      'Employment end date cannot be before the start date.',
      related,
    );
  };
  const validateExpenseDates = (startPath: string, endPath: string) => {
    const startDate = dateAt(startPath);
    const endDate = dateAt(endPath);
    if (!startDate || !endDate || startDate.getTime() <= endDate.getTime()) return;
    const related = [startPath, endPath];
    addMessageForExistingField(
      startPath,
      'error',
      'Expense start date cannot be after the end date.',
      related,
    );
    addMessageForExistingField(
      endPath,
      'error',
      'Expense end date cannot be before the start date.',
      related,
    );
  };

  const validateEmploymentDetails = (
    statusPath: string,
    employerPath: string,
    jobTitlePath: string,
  ) => {
    const status = rawValueAt(statusPath);
    const employerName = rawValueAt(employerPath);
    const jobTitle = rawValueAt(jobTitlePath);
    if (WORKING_EMPLOYMENT_STATUSES.has(status) && !employerName) {
      const related = [statusPath, employerPath];
      addMessageForExistingField(
        statusPath,
        'error',
        'Working employment status needs employer details.',
        related,
      );
      addMessageForExistingField(
        employerPath,
        'error',
        'Enter employer details for this employment status.',
        related,
      );
    }
    if (NON_WORKING_EMPLOYMENT_STATUSES.has(status) && (employerName || jobTitle)) {
      const related = [statusPath, employerPath, jobTitlePath];
      addMessageForExistingField(
        statusPath,
        'warning',
        'Employment details conflict with a non-working status.',
        related,
      );
      if (employerName) {
        addMessageForExistingField(
          employerPath,
          'warning',
          'Clear employer details or change the employment status before planning.',
          related,
        );
      }
      if (jobTitle) {
        addMessageForExistingField(
          jobTitlePath,
          'warning',
          'Clear job title or change the employment status before planning.',
          related,
        );
      }
    }
  };

  const validateIncomeSourceAssumptions = () => {
    const incomeSourceIndexes = new Set<number>();
    for (const field of fields) {
      const match = field.path.match(/^sections\.income_sources\.(\d+)\./);
      if (match) incomeSourceIndexes.add(Number(match[1]));
    }

    for (const index of incomeSourceIndexes) {
      if (!rowShouldValidateRepeatableCompanions('sections.income_sources', index)) continue;

      const rootPath = `sections.income_sources.${index}`;
      const sourceTypePath = `${rootPath}.source_type`;
      const customSourceTypePath = `${rootPath}.custom_source_type`;
      const colaEnabledPath = `${rootPath}.cola_enabled`;
      const colaPercentPath = `${rootPath}.cola_percent`;
      const startTimingPath = `${rootPath}.start_timing`;
      const startMonthPath = `${rootPath}.start_month`;
      const claimingAgePath = `${rootPath}.claiming_age`;
      const endTimingPath = `${rootPath}.end_timing`;
      const endMonthPath = `${rootPath}.end_month`;
      const sourceType = rawValueAt(sourceTypePath);
      const startTiming = rawValueAt(startTimingPath);
      const endTiming = rawValueAt(endTimingPath);

      if (sourceType === 'CUSTOM' && !hasValueAt(customSourceTypePath)) {
        addMessageForExistingField(customSourceTypePath, 'error', 'Enter the custom source type.', [
          sourceTypePath,
          customSourceTypePath,
        ]);
      }

      if (rawValueAt(colaEnabledPath) === 'true' && !hasValueAt(colaPercentPath)) {
        addMessageForExistingField(colaPercentPath, 'error', 'Enter a COLA percent.', [
          colaEnabledPath,
          colaPercentPath,
        ]);
      }

      if (
        SOCIAL_SECURITY_START_TIMINGS.has(startTiming) &&
        sourceType !== INCOME_SOURCE_SOCIAL_SECURITY_TYPE
      ) {
        addMessageForExistingField(
          startTimingPath,
          'error',
          'FRA and claiming-age timing are only available for Social Security.',
          [sourceTypePath, startTimingPath],
        );
      }

      if (startTiming === 'SPECIFIC_MONTH' && !hasValueAt(startMonthPath)) {
        addMessageForExistingField(startMonthPath, 'error', 'Enter a start month.', [
          startTimingPath,
          startMonthPath,
        ]);
      }

      if (startTiming === 'CLAIMING_AGE' && !hasValueAt(claimingAgePath)) {
        addMessageForExistingField(claimingAgePath, 'error', 'Enter a claiming age.', [
          startTimingPath,
          claimingAgePath,
        ]);
      }

      if (endTiming === 'SPECIFIC_MONTH' && !hasValueAt(endMonthPath)) {
        addMessageForExistingField(endMonthPath, 'error', 'Enter an end month.', [
          endTimingPath,
          endMonthPath,
        ]);
      }

      const startMonth = monthAt(startMonthPath);
      const endMonth = monthAt(endMonthPath);
      if (
        startTiming === 'SPECIFIC_MONTH' &&
        endTiming === 'SPECIFIC_MONTH' &&
        startMonth &&
        endMonth &&
        monthSortValue(startMonth) > monthSortValue(endMonth)
      ) {
        const related = [startMonthPath, endMonthPath];
        addMessageForExistingField(
          startMonthPath,
          'error',
          'Start month cannot be after the end month.',
          related,
        );
        addMessageForExistingField(
          endMonthPath,
          'error',
          'End month cannot be before the start month.',
          related,
        );
      }
    }
  };

  validateEmploymentDates(
    'sections.investor.employment.started_on',
    'sections.investor.employment.ended_on',
  );
  validateEmploymentDates(
    'sections.spouse.employment.started_on',
    'sections.spouse.employment.ended_on',
  );
  validateEmploymentDetails(
    'sections.investor.employment.employment_status',
    'sections.investor.employment.employer_name',
    'sections.investor.employment.job_title',
  );
  validateEmploymentDetails(
    'sections.spouse.employment.employment_status',
    'sections.spouse.employment.employer_name',
    'sections.spouse.employment.job_title',
  );
  for (const startPath of fields
    .map((field) => field.path)
    .filter((path) => /^sections\.expenses\.\d+\.start_date$/.test(path))) {
    validateExpenseDates(startPath, startPath.replace(/\.start_date$/, '.end_date'));
  }
  REPEATABLE_COMPANION_REQUIREMENTS.forEach(validateRepeatableCompanions);
  validateIncomeSourceAssumptions();

  for (const path of ['sections.investor.date_of_birth', 'sections.spouse.date_of_birth']) {
    const birthDate = dateAt(path);
    if (birthDate && birthDate.getTime() > today.getTime()) {
      addMessageForExistingField(path, 'error', 'Date of birth cannot be in the future.');
    }
  }

  const investorBirthDate = dateAt('sections.investor.date_of_birth');
  const spouseBirthDate = dateAt('sections.spouse.date_of_birth');
  const investorTargetAge = numberAt('sections.retirement.target_age');
  const spouseTargetAge = numberAt('sections.retirement.spouse_target_age');
  if (investorBirthDate && investorTargetAge !== null) {
    const currentAge = ageOnDate(investorBirthDate, today);
    if (investorTargetAge <= currentAge) {
      addMessageForExistingField(
        'sections.retirement.target_age',
        'error',
        "Target retirement age must be greater than the investor's current age.",
        ['sections.investor.date_of_birth', 'sections.retirement.target_age'],
      );
    }
  }
  if (spouseBirthDate && spouseTargetAge !== null) {
    const currentAge = ageOnDate(spouseBirthDate, today);
    if (spouseTargetAge <= currentAge) {
      addMessageForExistingField(
        'sections.retirement.spouse_target_age',
        'error',
        "Spouse target retirement age must be greater than the spouse's current age.",
        ['sections.spouse.date_of_birth', 'sections.retirement.spouse_target_age'],
      );
    }
  }

  for (const path of fields
    .map((field) => field.path)
    .filter((path) => /^sections\.goals\.\d+\.target_date$/.test(path))) {
    const targetDate = dateAt(path);
    if (targetDate && targetDate.getTime() < today.getTime()) {
      addMessageForExistingField(
        path,
        'warning',
        'Target date is in the past; update it before using this goal for planning.',
      );
    }
  }

  const maritalStatus = rawValueAt('sections.investor.marital_status');
  const taxFilingStatus = rawValueAt('sections.tax.filing_status');
  if (
    MARRIED_FILING_STATUSES.has(taxFilingStatus) &&
    SPOUSE_INCOMPATIBLE_MARITAL_STATUSES.has(maritalStatus)
  ) {
    const related = ['sections.investor.marital_status', 'sections.tax.filing_status'];
    addMessageForExistingField(
      'sections.investor.marital_status',
      'error',
      'Marital status conflicts with married tax filing status.',
      related,
    );
    addMessageForExistingField(
      'sections.tax.filing_status',
      'error',
      'Married filing status requires spouse-compatible marital status.',
      related,
    );
  }

  const hasSpouseIdentity = [
    'sections.spouse.name.given_name',
    'sections.spouse.name.family_name',
    'sections.spouse.date_of_birth',
  ].some((path) => hasValueAt(path));
  const spouseContextPresent =
    hasSpouseIdentity || SPOUSE_COMPATIBLE_MARITAL_STATUSES.has(maritalStatus);
  const spouseDependentPaths = fields
    .map((field) => field.path)
    .filter((path) => {
      if (path === 'sections.retirement.spouse_target_age') return hasValueAt(path);
      if (path === 'sections.spouse.annual_gross_income') return hasValueAt(path);
      if (path === 'sections.spouse.employment.employment_status') return hasValueAt(path);
      if (path.endsWith('.joint_with_spouse') || path.endsWith('.shared_with_spouse')) {
        return rawValueAt(path) === 'true';
      }
      return false;
    });
  if (!spouseContextPresent && spouseDependentPaths.length > 0) {
    for (const path of spouseDependentPaths) {
      addMessageForExistingField(
        path,
        'warning',
        'This field depends on spouse details. Add spouse information or update marital status before planning.',
        spouseDependentPaths,
      );
    }
  }

  const incomeAmountPaths = fields
    .map((field) => field.path)
    .filter((path) => /^sections\.income_sources\.\d+\.(gross_annual_amount|amount)$/.test(path));
  const annualIncomePaths = [...incomeAmountPaths, 'sections.spouse.annual_gross_income'].filter(
    (path) => numberAt(path) !== null,
  );
  const annualizedRepeatableAmount = (path: string): number => {
    const amount = numberAt(path) ?? 0;
    if (path.endsWith('.gross_annual_amount')) return amount;
    if (path.startsWith('sections.income_sources.') || path.startsWith('sections.expenses.')) {
      const frequencyPath = path.replace(/\.amount$/, '.frequency');
      return amount * annualizationFactor(rawValueAt(frequencyPath));
    }
    return amount;
  };
  const knownAnnualIncome = annualIncomePaths.reduce(
    (total, path) => total + annualizedRepeatableAmount(path),
    0,
  );
  const namedExpensePaths = fields
    .map((field) => field.path)
    .filter((path) => /^sections\.expenses\.\d+\.amount$/.test(path))
    .filter((path) => numberAt(path) !== null);
  const expensePaths = [
    'sections.expenses.total_fixed_monthly',
    'sections.expenses.total_variable_monthly',
    'sections.expenses.annual_one_time',
    ...namedExpensePaths,
  ].filter((path) => numberAt(path) !== null);
  const knownAnnualExpenses =
    (numberAt('sections.expenses.total_fixed_monthly') ?? 0) * 12 +
    (numberAt('sections.expenses.total_variable_monthly') ?? 0) * 12 +
    (numberAt('sections.expenses.annual_one_time') ?? 0) +
    namedExpensePaths.reduce((total, path) => total + annualizedRepeatableAmount(path), 0);
  if (knownAnnualExpenses > 0 && knownAnnualIncome === 0 && expensePaths.length > 0) {
    for (const path of expensePaths) {
      addMessageForExistingField(
        path,
        'warning',
        'Expenses are entered without known annual income. Add income sources or confirm expenses before planning.',
        expensePaths,
      );
    }
  }

  const desiredRetirementSpending = numberAt('sections.retirement.desired_annual_spending');
  if (
    desiredRetirementSpending !== null &&
    knownAnnualIncome > 0 &&
    desiredRetirementSpending > knownAnnualIncome * 2
  ) {
    const related = ['sections.retirement.desired_annual_spending', ...annualIncomePaths];
    for (const path of related) {
      addMessageForExistingField(
        path,
        'warning',
        'Desired retirement spending is much higher than known annual income. Confirm the income or retirement spending assumptions.',
        related,
      );
    }
  }

  return validationResultFromMessages(messages);
};

const parseFormValue = (
  field: ProfilePayloadInputField,
  rawValue: string | undefined,
): string | number | boolean | string[] | null => {
  const value = (rawValue ?? '').trim();
  if (!value) return field.valueKind === 'string-list' ? [] : null;

  if (field.valueKind === 'money') {
    return normalizedDecimalFormValue(value);
  }

  if (field.valueKind === 'percent') {
    return normalizedDecimalFormValue(value);
  }

  if (field.valueKind === 'string-list') {
    return stringListValuesFromFormValue(value);
  }

  if (field.inputKind === 'number') {
    return Number(value);
  }

  if (field.inputKind === 'boolean') {
    return value === 'true';
  }

  return value;
};

const setNestedValue = (
  target: Record<string, unknown> | unknown[],
  path: string,
  value: unknown,
) => {
  const segments = path.split('.');
  let cursor: Record<string, unknown> | unknown[] = target;

  for (let index = 0; index < segments.length - 1; index += 1) {
    const segment = segments[index];
    const nextSegment = segments[index + 1];
    if (Array.isArray(cursor) && /^\d+$/.test(segment)) {
      const arrayIndex = Number(segment);
      const currentValue = cursor[arrayIndex];
      if (!isRecord(currentValue) && !Array.isArray(currentValue)) {
        cursor[arrayIndex] = /^\d+$/.test(nextSegment) ? [] : {};
      }
      cursor = cursor[arrayIndex] as Record<string, unknown> | unknown[];
      continue;
    }

    if (Array.isArray(cursor)) return;

    const currentValue = cursor[segment];
    if (!isRecord(currentValue) && !Array.isArray(currentValue)) {
      cursor[segment] = /^\d+$/.test(nextSegment) ? [] : {};
    }
    cursor = cursor[segment] as Record<string, unknown> | unknown[];
  }

  const finalSegment = segments[segments.length - 1];
  if (Array.isArray(cursor) && /^\d+$/.test(finalSegment)) {
    cursor[Number(finalSegment)] = value;
    return;
  }
  if (!Array.isArray(cursor)) {
    cursor[finalSegment] = value;
  }
};

const patchPathForField = (path: string): string => path.replace(/^sections\./, '');

const namePartKey = (path: string): (typeof NAME_PART_KEYS)[number] | null => {
  const segments = path.split('.');
  const key = segments[segments.length - 1] ?? '';
  return (NAME_PART_KEYS as readonly string[]).includes(key)
    ? (key as (typeof NAME_PART_KEYS)[number])
    : null;
};

const valuesEqual = (
  left: string | number | boolean | string[] | null,
  right: ProfilePayloadOriginalValue,
): boolean => {
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    return left.length === right.length && left.every((item, index) => item === right[index]);
  }
  return Object.is(left, right);
};

const cloneJsonValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map((item) => cloneJsonValue(item));
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, cloneJsonValue(item)]),
    );
  }
  return value;
};

const patchValueForField = (
  field: ProfilePayloadInputField,
  value: string | number | boolean | string[] | null,
): unknown => {
  if (field.valueKind === 'money') {
    return value === null ? null : { amount: value, currency: field.currency ?? 'USD' };
  }
  if (field.valueKind === 'percent') {
    return value === null ? null : { percent: value };
  }
  return value;
};

const nameValueForRoot = (
  root: string,
  originalValue: unknown,
  fields: ProfilePayloadInputField[],
  parsedValues: Map<string, string | number | boolean | string[] | null>,
): Record<(typeof NAME_PART_KEYS)[number], string | null> => {
  // A scoped operation may update one part of the shared name root.
  const nameValues = namePartsFromValue(originalValue);

  for (const field of fields) {
    if (field.groupPath !== root) continue;
    const key = namePartKey(field.path);
    if (key === null) continue;
    const value = parsedValues.get(field.path);
    nameValues[key] = typeof value === 'string' ? value : null;
  }

  if (!Object.values(nameValues).some((value) => value !== null)) {
    throw new Error(`${labelForPath(root)} must include at least one name part.`);
  }
  return nameValues;
};

const syncNameDisplayValues = (value: unknown): void => {
  if (Array.isArray(value)) {
    value.forEach(syncNameDisplayValues);
    return;
  }
  if (!isRecord(value)) return;

  const hasNameParts = NAME_PART_KEYS.some((key) =>
    Object.prototype.hasOwnProperty.call(value, key),
  );
  if (hasNameParts || Object.prototype.hasOwnProperty.call(value, 'display_name')) {
    const displayName =
      NAME_PART_KEYS.map((key) => value[key])
        .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
        .join(' ') || null;
    value.display_name = displayName;
  }

  Object.values(value).forEach(syncNameDisplayValues);
};

const rootForChangedField = (field: ProfilePayloadInputField): string => {
  const segments = field.path.split('.');
  const numericIndex = segments.findIndex((segment) => /^\d+$/.test(segment));
  if (numericIndex > 0) return segments.slice(0, numericIndex).join('.');

  if (field.path.startsWith('sections.spouse.')) return 'sections.spouse';

  const employmentIndex = segments.indexOf('employment');
  if (employmentIndex > -1) return segments.slice(0, employmentIndex + 1).join('.');

  const addressIndex = segments.findIndex((segment) =>
    ['primary_residence_address', 'mailing_address'].includes(segment),
  );
  if (addressIndex > -1) return segments.slice(0, addressIndex + 1).join('.');

  if (field.valueKind === 'name-part' && field.groupPath) return field.groupPath;

  return field.path;
};

const fieldsUnderRoot = (
  fields: ProfilePayloadInputField[],
  root: string,
): ProfilePayloadInputField[] =>
  fields.filter((field) => field.path === root || field.path.startsWith(`${root}.`));

const buildValueForRoot = (
  root: string,
  payload: InvestorProfilePayload,
  fields: ProfilePayloadInputField[],
  parsedValues: Map<string, string | number | boolean | string[] | null>,
): unknown => {
  const rootFields = fieldsUnderRoot(fields, root);
  const exactField = rootFields.find((field) => field.path === root);
  if (exactField && rootFields.length === 1) {
    return patchValueForField(exactField, parsedValues.get(exactField.path) ?? null);
  }

  if (root.endsWith('.name')) {
    return nameValueForRoot(root, getValueAtPath(payload, root), rootFields, parsedValues);
  }

  const rawRootValue = getValueAtPath(payload, root);
  const isArrayRoot =
    Array.isArray(rawRootValue) ||
    rootFields.some((field) => /^\d+(\.|$)/.test(field.path.slice(root.length + 1)));
  const rootValue = cloneJsonValue(rawRootValue);
  const value =
    (isArrayRoot && Array.isArray(rootValue)) || (!isArrayRoot && isRecord(rootValue))
      ? rootValue
      : isArrayRoot
        ? []
        : {};

  for (const field of rootFields) {
    if (field.path === root) continue;
    const relativePath = field.path.slice(root.length + 1);
    const parsedValue = parsedValues.get(field.path) ?? null;
    setNestedValue(
      value as Record<string, unknown>,
      relativePath,
      patchValueForField(field, parsedValue),
    );
  }

  syncNameDisplayValues(value);
  return value;
};

export const buildProfilePayloadPatch = ({
  payload,
  values,
  idempotencyKey,
  changedRootPaths = [],
  includedFieldPrefixes,
  disabledFieldPrefixes = [],
  validationDisabledFieldPrefixes = disabledFieldPrefixes,
  repeatableCompanionValidationFieldPaths,
}: {
  payload: InvestorProfilePayload;
  values: ProfilePayloadFormValues;
  idempotencyKey: string;
  changedRootPaths?: string[];
  includedFieldPrefixes?: readonly string[];
  disabledFieldPrefixes?: readonly string[];
  validationDisabledFieldPrefixes?: readonly string[];
  repeatableCompanionValidationFieldPaths?: readonly string[];
}): ProfilePayloadPatchBuildResult => {
  const update: Record<string, unknown> = {};
  const includesField = (path: string): boolean =>
    includedFieldPrefixes === undefined ||
    includedFieldPrefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}.`));
  const fields = editableProfilePayloadFieldsExcluding(payload, disabledFieldPrefixes).filter(
    (field) => includesField(field.path),
  );
  const validation = validateProfilePayloadForm({
    payload,
    values,
    disabledFieldPrefixes: validationDisabledFieldPrefixes,
    repeatableCompanionValidationFieldPaths,
  });
  const validationFields = editableProfilePayloadFieldsExcluding(
    payload,
    validationDisabledFieldPrefixes,
  );
  // Keep the full profile as validation context (for example birth date for retirement age),
  // while only blocking on rules involving fields owned by this operation.
  const scopedValidation = validationResultFromMessages(
    validation.messages.filter(
      (message) =>
        includesField(message.fieldPath) || message.relatedFieldPaths?.some(includesField),
    ),
  );
  const firstValidationError = profilePayloadValidationErrorMessage(
    scopedValidation,
    validationFields,
  );
  if (firstValidationError) {
    throw new Error(firstValidationError);
  }

  const parsedValues = new Map<string, string | number | boolean | string[] | null>();
  const changedRoots = new Set<string>(
    changedRootPaths.filter(
      (path) =>
        includesField(path) ||
        includedFieldPrefixes?.some((prefix) => prefix.startsWith(`${path}.`)),
    ),
  );

  for (const field of fields) {
    const nextValue = parseFormValue(field, values[field.path] ?? field.value);
    parsedValues.set(field.path, nextValue);
    if (!valuesEqual(nextValue, field.originalValue)) {
      changedRoots.add(rootForChangedField(field));
    }
  }

  if (changedRoots.size === 0) {
    throw new Error('Change at least one profile field before applying update.');
  }

  const changedPaths = [...changedRoots].sort((left, right) => left.localeCompare(right));
  for (const root of changedPaths) {
    setNestedValue(
      update,
      patchPathForField(root),
      buildValueForRoot(root, payload, fields, parsedValues),
    );
  }

  return {
    patch: {
      expected_revision: getProfilePayloadRevision(payload),
      idempotency_key: idempotencyKey,
      update,
    },
    changedPaths,
  };
};
